import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
    Sparkles,
    ArrowRight,
    Bot,
    FolderTree,
    HelpCircle,
    BookOpen,
    Users,
    Share2,
    History,
    Pin,
    Bell,
    Quote,
    CheckCircle2,
    Briefcase,
    Plane,
    Monitor,
    MessageCircle,
    Heart,
    GraduationCap,
    Film,
    Sun,
    Leaf,
    Utensils,
    Wallet,
    Dumbbell,
    Landmark,
    Palette,
    Clock,
    Trophy,
    Volume2,
    Network,
    Target,
    TrendingUp,
    Filter,
    Search,
} from "lucide-react";
import { SEO } from "@/components/seo/SEO";
import { BentoCard, BentoGrid } from "@/components/marketing/BentoGrid";
import { GlassmorphicCard, GradientText, FloatingShapes } from "@/components/marketing/GlassmorphicCard";
import { getAppUrl } from "@lyder-talk/core";
import { motion } from "framer-motion";

const Features = () => {
    const categories = [
        { icon: Briefcase, name: "Business", color: "from-slate-500 to-gray-600" },
        { icon: Plane, name: "Travel", color: "from-sky-500 to-blue-600" },
        { icon: Monitor, name: "Technology", color: "from-indigo-500 to-blue-600" },
        { icon: MessageCircle, name: "Social", color: "from-pink-500 to-rose-600" },
        { icon: Heart, name: "Lifestyle", color: "from-red-500 to-pink-600" },
        { icon: GraduationCap, name: "Education", color: "from-cyan-500 to-indigo-600" },
        { icon: Film, name: "Entertainment", color: "from-amber-500 to-orange-600" },
        { icon: Sun, name: "Daily Life", color: "from-yellow-500 to-amber-600" },
        { icon: Leaf, name: "Environment", color: "from-blue-500 to-indigo-600" },
        { icon: Utensils, name: "Food & Cooking", color: "from-orange-500 to-red-600" },
        { icon: Wallet, name: "Finance", color: "from-amber-500 to-orange-600" },
        { icon: Dumbbell, name: "Health & Fitness", color: "from-rose-500 to-red-600" },
        { icon: Landmark, name: "Politics", color: "from-blue-600 to-indigo-700" },
        { icon: Palette, name: "Art & Design", color: "from-fuchsia-500 to-pink-600" },
        { icon: Clock, name: "History", color: "from-amber-600 to-yellow-700" },
        { icon: Trophy, name: "Sports", color: "from-cyan-500 to-blue-600" },
    ];

    const mainFeatures = [
        {
            icon: Bot,
            title: "AI-Powered Topic Generation",
            description: "Generate random or custom conversation topics with AI assistance. Get personalized topics based on your interests and skill level.",
            gradient: "from-blue-500 to-sky-600",
            highlights: ["Random topic generation", "Custom topic creation", "AI-powered suggestions", "Skill-level adaptation"],
            size: "large" as const,
        },
        {
            icon: FolderTree,
            title: "Smart Category System",
            description: "Explore 16 diverse categories covering everything from Business to Sports.",
            gradient: "from-blue-500 to-cyan-600",
            highlights: ["16 categories", "Easy navigation"],
            size: "default" as const,
        },
        {
            icon: HelpCircle,
            title: "Interactive Questions",
            description: "Practice with questions at different difficulty levels.",
            gradient: "from-blue-500 to-indigo-600",
            highlights: ["Multiple levels", "Real scenarios"],
            size: "default" as const,
        },
        {
            icon: BookOpen,
            title: "Vocabulary Building",
            description: "Learn new words with IPA pronunciation, definitions, and examples.",
            gradient: "from-orange-500 to-amber-600",
            highlights: ["IPA pronunciation", "CEFR levels", "Mind map view"],
            size: "wide" as const,
        },
        {
            icon: Share2,
            title: "Community Sharing",
            description: "Share your practice sessions publicly or on social media.",
            gradient: "from-pink-500 to-rose-600",
            highlights: ["Public sessions", "Social sharing"],
            size: "default" as const,
        },
        {
            icon: Users,
            title: "Study Groups",
            description: "Join or create study groups for collaborative learning.",
            gradient: "from-indigo-500 to-blue-600",
            highlights: ["Group creation", "Role management"],
            size: "default" as const,
        },
        {
            icon: History,
            title: "Progress Tracking",
            description: "Monitor your learning journey with detailed history and analytics.",
            gradient: "from-cyan-500 to-blue-600",
            highlights: ["Session history", "Analytics"],
            size: "default" as const,
        },
        {
            icon: Pin,
            title: "Session Management",
            description: "Pin important sessions, filter by category, and search past topics.",
            gradient: "from-amber-500 to-yellow-600",
            highlights: ["Pin sessions", "Advanced filtering"],
            size: "default" as const,
        },
        {
            icon: Bot,
            title: "AI Assistant",
            description: "Get contextual help and guidance from our AI assistant.",
            gradient: "from-fuchsia-500 to-pink-600",
            highlights: ["Contextual help", "24/7 availability"],
            size: "wide" as const,
        },
        {
            icon: Bell,
            title: "Real-time Notifications",
            description: "Stay updated with notifications for group activities and mentions.",
            gradient: "from-red-500 to-rose-600",
            highlights: ["Push notifications", "Activity alerts"],
            size: "default" as const,
        },
        {
            icon: Quote,
            title: "Speaking Guide",
            description: "AI-generated speaking guides with natural expressions and tips.",
            gradient: "from-slate-500 to-gray-600",
            highlights: ["Natural expressions", "Grammar spotlight"],
            size: "default" as const,
        },
    ];

    const vocabularyFeatures = [
        { icon: Volume2, title: "IPA Pronunciation", description: "Learn correct pronunciation with International Phonetic Alphabet" },
        { icon: BookOpen, title: "Definitions & Examples", description: "Comprehensive definitions with contextual examples" },
        { icon: Target, title: "CEFR Levels", description: "Vocabulary tagged with Common European Framework levels" },
        { icon: Network, title: "Mind Map View", description: "Visualize word relationships and connections" },
    ];

    return (
        <>
            <SEO
                title="Features - Lyder Talk | AI-Powered Speaking Practice Platform"
                description="Discover all features of Lyder Talk: AI topic generation, 16 categories, interactive questions, vocabulary building, study groups, progress tracking, and more."
                url="/features"
                keywords="language learning features, speaking practice, AI topics, vocabulary builder, study groups, progress tracking, CEFR levels"
            />

            <div className="bg-background overflow-hidden">
                {/* Dynamic Background */}
                <div className="fixed inset-0 pointer-events-none">
                    <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-background" />
                    <FloatingShapes />
                    <div className="absolute inset-0 grid-pattern opacity-30" />
                    <div className="absolute inset-0 noise-overlay" />
                </div>

                {/* Hero Section */}
                <section className="relative z-10 container mx-auto px-4 py-16 sm:py-24">
                    <div className="max-w-4xl mx-auto text-center">
                        <Badge variant="secondary" className="mb-6 px-4 py-1.5 text-sm font-medium backdrop-blur-sm border border-primary/20 animate-fade-in-up">
                            <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                            Explore All Features
                        </Badge>

                        <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 leading-tight animate-fade-in-up animation-delay-100">
                            <GradientText>
                                Everything You Need
                            </GradientText>
                            <br />
                            <span className="text-foreground">To Master Speaking</span>
                        </h1>

                        <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto mb-8 leading-relaxed animate-fade-in-up animation-delay-200">
                            Discover all the powerful features that make Lyder Talk the ultimate platform for improving your speaking skills.
                        </p>
                    </div>
                </section>

                {/* Main Features - Bento Grid */}
                <section className="relative z-10 container mx-auto px-4 pb-16">
                    <div className="text-center mb-12">
                        <Badge variant="outline" className="mb-4 backdrop-blur-sm border-primary/20 animate-fade-in-up">Core Features</Badge>
                        <h2 className="text-3xl sm:text-4xl font-bold mb-4 animate-fade-in-up animation-delay-100">
                            Powerful Tools for
                            <GradientText> Learning</GradientText>
                        </h2>
                    </div>

                    <BentoGrid className="max-w-7xl mx-auto">
                        {mainFeatures.map((feature, index) => {
                            const Icon = feature.icon;
                            return (
                                <BentoCard
                                    key={feature.title}
                                    size={feature.size}
                                    className="animate-fade-in-up"
                                    style={{ animationDelay: `${index * 50}ms` }}
                                >
                                    <div className={`p-6 h-full flex flex-col ${feature.size === 'large' ? 'md:p-8' : ''}`}>
                                        <div className={`w-12 h-12 ${feature.size === 'large' ? 'md:w-16 md:h-16' : ''} rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                                            <Icon className={`w-6 h-6 ${feature.size === 'large' ? 'md:w-8 md:h-8' : ''} text-white`} />
                                        </div>
                                        <h3 className={`text-lg ${feature.size === 'large' ? 'md:text-2xl' : ''} font-semibold mb-2 group-hover:text-primary transition-colors duration-300`}>{feature.title}</h3>
                                        <p className="text-muted-foreground leading-relaxed text-sm mb-4 flex-grow">{feature.description}</p>
                                        <div className="flex flex-wrap gap-2 mt-auto">
                                            {feature.highlights.map((highlight) => (
                                                <Badge key={highlight} variant="secondary" className="text-xs">
                                                    <CheckCircle2 className="w-3 h-3 mr-1" />
                                                    {highlight}
                                                </Badge>
                                            ))}
                                        </div>
                                    </div>
                                </BentoCard>
                            );
                        })}
                    </BentoGrid>
                </section>

                {/* Categories Section */}
                <section className="relative py-20 md:py-32">
                    <div className="absolute inset-0 bg-gradient-to-b from-muted/50 via-muted/30 to-background" />
                    <div className="container mx-auto px-4 relative z-10">
                        <div className="text-center mb-16">
                            <Badge variant="outline" className="mb-4 backdrop-blur-sm border-primary/20 animate-fade-in-up">Categories</Badge>
                            <h2 className="text-3xl md:text-4xl font-bold mb-4 animate-fade-in-up animation-delay-100">
                                16 Diverse{" "}
                                <GradientText>Categories</GradientText>
                            </h2>
                            <p className="text-lg text-muted-foreground max-w-2xl mx-auto animate-fade-in-up animation-delay-200">
                                Choose from a wide range of topics to practice conversations that matter to you
                            </p>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-4 max-w-6xl mx-auto">
                            {categories.map((category, index) => {
                                const Icon = category.icon;
                                return (
                                    <GlassmorphicCard
                                        key={category.name}
                                        padding="sm"
                                        className="flex flex-col items-center text-center animate-fade-in-up cursor-pointer"
                                        style={{ animationDelay: `${index * 30}ms` }}
                                    >
                                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${category.color} flex items-center justify-center mb-2 shadow-md group-hover:scale-110 transition-transform duration-300`}>
                                            <Icon className="w-6 h-6 text-white" />
                                        </div>
                                        <span className="text-sm font-medium text-center group-hover:text-primary transition-colors duration-300">{category.name}</span>
                                    </GlassmorphicCard>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* Vocabulary Features */}
                <section className="relative z-10 container mx-auto px-4 py-16 sm:py-24">
                    <div className="text-center mb-12">
                        <Badge variant="outline" className="mb-4 backdrop-blur-sm border-primary/20 animate-fade-in-up">Vocabulary</Badge>
                        <h2 className="text-3xl sm:text-4xl font-bold mb-4 animate-fade-in-up animation-delay-100">
                            Build Your
                            <GradientText> Vocabulary</GradientText>
                        </h2>
                        <p className="text-muted-foreground max-w-2xl mx-auto animate-fade-in-up animation-delay-200">
                            Learn new words effectively with comprehensive vocabulary tools
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
                        {vocabularyFeatures.map((feature, index) => {
                            const Icon = feature.icon;
                            return (
                                <GlassmorphicCard
                                    key={feature.title}
                                    padding="md"
                                    className="text-center animate-fade-in-up"
                                    style={{ animationDelay: `${index * 100}ms` }}
                                >
                                    <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 transition-colors">
                                        <Icon className="w-8 h-8 text-primary" />
                                    </div>
                                    <h3 className="font-semibold mb-2 group-hover:text-primary transition-colors duration-300">{feature.title}</h3>
                                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                                </GlassmorphicCard>
                            );
                        })}
                    </div>
                </section>

                {/* Session Management Features */}
                <section className="relative py-16">
                    <div className="absolute inset-0 bg-gradient-to-b from-muted/50 via-muted/30 to-background" />
                    <div className="container mx-auto px-4 relative z-10">
                        <div className="max-w-4xl mx-auto">
                            <div className="text-center mb-12">
                                <Badge variant="outline" className="mb-4 backdrop-blur-sm border-primary/20 animate-fade-in-up">Organization</Badge>
                                <h2 className="text-3xl sm:text-4xl font-bold mb-4 animate-fade-in-up animation-delay-100">
                                    Manage Your
                                    <GradientText> Sessions</GradientText>
                                </h2>
                            </div>

                            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6">
                                {[
                                    { icon: Pin, title: "Pin Sessions", description: "Keep important sessions at the top" },
                                    { icon: Filter, title: "Advanced Filters", description: "Filter by category, date, or status" },
                                    { icon: Search, title: "Quick Search", description: "Find any session instantly" },
                                    { icon: TrendingUp, title: "Sort Options", description: "Sort by date, name, or activity" },
                                ].map((item, index) => {
                                    const Icon = item.icon;
                                    return (
                                        <GlassmorphicCard
                                            key={item.title}
                                            padding="md"
                                            className="flex flex-col items-center text-center animate-fade-in-up"
                                            style={{ animationDelay: `${index * 100}ms` }}
                                        >
                                            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-3 group-hover:bg-primary/20 transition-colors">
                                                <Icon className="w-6 h-6 text-primary" />
                                            </div>
                                            <h3 className="font-semibold mb-1 group-hover:text-primary transition-colors duration-300">{item.title}</h3>
                                            <p className="text-sm text-muted-foreground">{item.description}</p>
                                        </GlassmorphicCard>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </section>

                {/* Signup Section */}
                <section className="relative z-10 container mx-auto px-4 py-16 sm:py-24">
                    <GlassmorphicCard 
                        className="max-w-2xl mx-auto"
                        padding="none"
                        animatedBorder
                        hoverGradient="from-primary/10 via-secondary/5 to-accent/10"
                    >
                        <div className="p-8 sm:p-12 relative">
                            {/* Decorative elements */}
                            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/20 rounded-full blur-3xl -translate-y-32 translate-x-32 pointer-events-none animate-pulse-glow" />
                            <div className="absolute bottom-0 left-0 w-48 h-48 bg-secondary/20 rounded-full blur-3xl translate-y-24 -translate-x-24 pointer-events-none animate-pulse-glow animation-delay-1000" />

                            <div className="relative z-10 text-center">
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                >
                                    <Badge variant="secondary" className="mb-4 backdrop-blur-sm border border-primary/20">
                                        <Sparkles className="w-3 h-3 mr-1" />
                                        Get Started
                                    </Badge>
                                    <h2 className="text-3xl sm:text-4xl font-bold mb-4">
                                        Ready to Start Learning?
                                    </h2>
                                    <p className="text-muted-foreground max-w-xl mx-auto mb-8 text-lg">
                                        Create your free account and start practicing with AI-powered topics right away.
                                    </p>
                                    <Button
                                        size="lg"
                                        onClick={() => {
                                            window.location.href = getAppUrl('/auth/signup');
                                        }}
                                        className="h-14 text-lg gap-2 shadow-xl hover-glow-primary"
                                    >
                                        <Sparkles className="w-5 h-5" />
                                        Sign Up Free
                                    </Button>
                                </motion.div>
                            </div>
                        </div>
                    </GlassmorphicCard>
                </section>
            </div>
        </>
    );
};

export default Features;


